import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUserSettings } from '@/components/user-settings-context'

// =============================================================================
// Helpers
// =============================================================================

function formatTime(ms: number): string {
  const negative = ms < 0
  const abs = Math.abs(ms)
  const totalSeconds = Math.floor(abs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const formatted = [
    String(hours).padStart(2, '0'),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0'),
  ].join(':')
  return negative ? `-${formatted}` : formatted
}

// =============================================================================
// Animated preview counter
// =============================================================================

interface AnimatedCounterProps {
  startMs: number
  direction: 'up' | 'down'
  className?: string
}

function AnimatedCounter({
  startMs,
  direction,
  className,
}: AnimatedCounterProps) {
  const [ms, setMs] = useState(startMs)
  const startMsRef = useRef(startMs)
  const mountTimeRef = useRef(Date.now())

  useEffect(() => {
    startMsRef.current = startMs
    mountTimeRef.current = Date.now()
    setMs(startMs)
  }, [startMs])

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - mountTimeRef.current
      if (direction === 'up') {
        setMs(startMsRef.current + elapsed)
      } else {
        setMs(startMsRef.current - elapsed)
      }
    }, 100)
    return () => clearInterval(id)
  }, [direction])

  return <span className={className}>{formatTime(ms)}</span>
}

// =============================================================================
// Props
// =============================================================================

export interface SessionTransitionDialogProps {
  open: boolean
  onStartFresh: () => void
  onContinue: () => void
  onDismiss: () => void
  stopwatchMs: number
  timerMs: number
  timerDurationMs: number
  hasTimer: boolean
  recordOverage: boolean
}

// =============================================================================
// Component
// =============================================================================

export function SessionTransitionDialog({
  open,
  onStartFresh,
  onContinue,
  onDismiss,
  stopwatchMs,
  timerMs,
  timerDurationMs,
  hasTimer,
  recordOverage,
}: SessionTransitionDialogProps) {
  const { settings, updateSettings } = useUserSettings()
  const [dontShowAgain, setDontShowAgain] = useState(false)

  // Reset checkbox on dismiss
  const handleDismiss = () => {
    setDontShowAgain(false)
    onDismiss()
  }

  const handleContinue = () => {
    if (dontShowAgain) {
      updateSettings({ timer: { showSessionTransitionNotice: false } })
    }
    onContinue()
  }

  const handleStartFresh = () => {
    if (dontShowAgain) {
      // "Start Fresh" = auto-reset behavior — enable it as the default
      updateSettings({
        timer: { showSessionTransitionNotice: false, autoReset: true },
      })
    }
    onStartFresh()
  }

  // Determine reset target for timer in "Start Fresh" preview
  const resetTimerTo = settings.timer.resetTimerTo
  const freshTimerMs = resetTimerTo === 'zero' ? 0 : timerDurationMs

  // Timer status display
  const timerExpiredNoOverage = hasTimer && timerMs <= 0 && !recordOverage
  const timerInOverage = hasTimer && timerMs < 0 && recordOverage

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleDismiss()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>You're about to start a new session</DialogTitle>
          <DialogDescription>
            Your previous session is complete. How would you like to continue?
          </DialogDescription>
        </DialogHeader>

        {/* Current values info */}
        <div className="bg-muted/50 rounded-md border px-4 py-3 text-sm space-y-1">
          <div className="text-muted-foreground flex items-center gap-2">
            <span className="font-medium text-foreground">Stopwatch:</span>
            <span className="font-mono">{formatTime(stopwatchMs)}</span>
          </div>
          {hasTimer && (
            <div className="text-muted-foreground flex items-center gap-2">
              <span className="font-medium text-foreground">Timer:</span>
              {timerExpiredNoOverage ? (
                <span>Timer has reached zero</span>
              ) : (
                <span className="font-mono">
                  {timerInOverage ? '-' : ''}
                  {formatTime(Math.abs(timerMs))}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Two choice buttons */}
        <div className="grid grid-cols-2 gap-3">
          {/* Continue from current values */}
          <button
            type="button"
            onClick={handleContinue}
            className="group flex flex-col items-center gap-2 rounded-lg border-2 border-border bg-muted/30 p-4 text-center transition-colors hover:border-muted-foreground/50 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Continue
            </div>
            <AnimatedCounter
              startMs={stopwatchMs}
              direction="up"
              className="font-mono text-xl font-bold text-blue-500"
            />
            {hasTimer && !timerExpiredNoOverage && (
              <AnimatedCounter
                startMs={timerMs}
                direction="down"
                className={`font-mono text-sm font-semibold ${timerInOverage ? 'text-red-500' : 'text-orange-500'}`}
              />
            )}
            {hasTimer && timerExpiredNoOverage && (
              <span className="text-xs text-muted-foreground">
                Timer at zero
              </span>
            )}
          </button>

          {/* Start fresh */}
          <button
            type="button"
            onClick={handleStartFresh}
            className="group flex flex-col items-center gap-2 rounded-lg border-2 border-green-300 bg-green-50/50 p-4 text-center transition-colors hover:border-green-400 hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-green-800 dark:bg-green-950/20 dark:hover:border-green-700 dark:hover:bg-green-950/40"
          >
            <div className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">
              Start Fresh
            </div>
            <AnimatedCounter
              startMs={0}
              direction="up"
              className="font-mono text-xl font-bold text-blue-500"
            />
            {hasTimer && freshTimerMs > 0 && (
              <span className="font-mono text-sm font-semibold text-orange-500">
                {formatTime(freshTimerMs)}
              </span>
            )}
            {hasTimer && freshTimerMs === 0 && (
              <span className="text-xs text-muted-foreground">No timer</span>
            )}
          </button>
        </div>

        {/* Don't show again checkbox */}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="rounded border-border"
          />
          Remember my selection
        </label>
      </DialogContent>
    </Dialog>
  )
}
